import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';
import { DocumentService } from '~app/company/shared/service/document.service';
import { ClientNotification, ShowNotification } from '~app/shared/notifications';
import { InvoiceQueryService } from './legalEntityPracticeV3Invoices-query.service';
import {
  LoadInvoices,
  LoadInvoicesFailure,
  LoadInvoicesSuccess,
  LoadMemberId,
  LoadMemberIdFailure,
  LoadMemberIdSuccess,
  SeeLegalEntityInvoice,
  SeeLegalEntityInvoiceFailure,
  SeeLegalEntityInvoiceSuccess
} from './legalEntityPracticeV3Invoices.action';
import { Invoice, LegalEntityPracticeV3InvoiceStateModel } from './legalEntityPracticeV3Invoices.state.model';

@State<LegalEntityPracticeV3InvoiceStateModel>({
  name: 'invoices',
  defaults: LegalEntityPracticeV3InvoiceStateModel.empty
})
@Injectable()
export class LegalEntityPracticeV3InvoicesState {
  constructor(
    private store: Store,
    private translate: TranslateService,
    private documentService: DocumentService,
    private queryService: InvoiceQueryService,
   ) {}

  @Selector()
  static invoices({ invoices }: LegalEntityPracticeV3InvoiceStateModel): Map<string, Invoice[]> {
    return invoices;
  }

  @Selector()
  static loading({ loading }: LegalEntityPracticeV3InvoiceStateModel): Map<string, boolean> {
    return loading;
  }

  @Selector()
  static memberId({ memberId }: LegalEntityPracticeV3InvoiceStateModel): string {
    return memberId;
  }

  @Selector()
  static loadingMember({ loadingMember }: LegalEntityPracticeV3InvoiceStateModel): boolean {
    return loadingMember;
  }

  @Action(LoadInvoices)
  loadInvoices(
    ctx: StateContext<LegalEntityPracticeV3InvoiceStateModel>,
    { legalEntityNumber, memberId }: LoadInvoices
  ) {
    this.UpdateInvoiceLoadingState(legalEntityNumber, true, ctx);
    return this.queryService.getInvoices(legalEntityNumber, memberId).pipe(
      tap((result) => {
        this.store.dispatch(new LoadInvoicesSuccess(result, legalEntityNumber));
      }),
      catchError((error) => this.store.dispatch(new LoadInvoicesFailure(error, legalEntityNumber)))
    );
  }

  @Action(LoadInvoicesSuccess)
  loadInvoicesSuccess(
    ctx: StateContext<LegalEntityPracticeV3InvoiceStateModel>,
    { legalEntityInvoices, legalEntityNumber }: LoadInvoicesSuccess,
  ) {
    this.UpdateInvoiceLoadingState(legalEntityNumber, false, ctx);
    this.UpdateInvoiceState(legalEntityNumber, legalEntityInvoices, ctx); 
  }

  @Action(LoadInvoicesFailure)
  loadInvoicesFailure(ctx: StateContext<LegalEntityPracticeV3InvoiceStateModel>, 
    { error, legalEntityNumber }: any) {
    this.UpdateInvoiceLoadingState(legalEntityNumber, false, ctx);
    this.UpdateInvoiceState(legalEntityNumber, [], ctx);
  }

  @Action(SeeLegalEntityInvoice)
  onSeeLegalEntityInvoice(ctx: StateContext<LegalEntityPracticeV3InvoiceStateModel>,
    { invoiceId, billNumber, memberId, legalEntityNumber }: SeeLegalEntityInvoice
  ) {
    ctx.dispatch(
      new ShowNotification(
        ClientNotification.info(
          this.translate.instant(`legalEntityPracticeV3Invoice.notification.seeLegalEntityInvoice`),
        )
      )
    );
     this.UpdateInvoiceLoadingState(legalEntityNumber, true, ctx);
    return this.queryService.getLegalEntityInvoiceReport(invoiceId, billNumber, memberId).pipe(
      tap((data) => ctx.dispatch(new SeeLegalEntityInvoiceSuccess(data, legalEntityNumber))),
      catchError((err) => ctx.dispatch(new SeeLegalEntityInvoiceFailure(err, legalEntityNumber)))
    );
  }

  @Action(SeeLegalEntityInvoiceSuccess)
  onSeeLegalEntityInvoiceSuccess(
     ctx: StateContext<LegalEntityPracticeV3InvoiceStateModel>,
    { data, legalEntityNumber }: SeeLegalEntityInvoiceSuccess
  ) {
    this.UpdateInvoiceLoadingState(legalEntityNumber, false, ctx);
    this.documentService.download({
      data: data,
      name: `LegalEntityInvoice.pdf`
    });
  }

  @Action(SeeLegalEntityInvoiceFailure)
  onSeeLegalEntityInvoiceFailure(
     ctx: StateContext<LegalEntityPracticeV3InvoiceStateModel>,
    { error, legalEntityNumber }: SeeLegalEntityInvoiceFailure
  ) {
    this.UpdateInvoiceLoadingState(legalEntityNumber, false, ctx);
    console.log(error);
  }


  @Action(LoadMemberId)
  onLoadMemberId(
    { patchState, dispatch }: StateContext<LegalEntityPracticeV3InvoiceStateModel>,
    { permitNumber }: LoadMemberId
  ) {
    patchState({ loadingMember: true });
    return this.queryService.getMemberIdByPermitNumber(permitNumber).pipe(
      tap((res) => dispatch(new LoadMemberIdSuccess(res))),
      catchError((error) => dispatch(new LoadMemberIdFailure(error)))
    );
  }
  @Action(LoadMemberIdSuccess)
  onLoadMemberIdSuccess(
    { patchState }: StateContext<LegalEntityPracticeV3InvoiceStateModel>,
    { data }: LoadMemberIdSuccess
  ) {
    patchState({ memberId: data, loadingMember: false });
  }

  @Action(LoadMemberIdFailure)
  onLoadMemberIdFailure(
    { patchState }: StateContext<LegalEntityPracticeV3InvoiceStateModel>,
    { error }: LoadMemberIdFailure
  ) {
    patchState({ memberId: null, loadingMember: false });
  }

  private UpdateInvoiceState(legalEntityNumber: string,
       invoices: Invoice[],  ctx: StateContext<LegalEntityPracticeV3InvoiceStateModel> 
  ) {
    const newInvoices = new Map(ctx.getState().invoices);
    newInvoices.set(legalEntityNumber, invoices);
    ctx.patchState({
      invoices: newInvoices
    });
  }

  private UpdateInvoiceLoadingState(legalEntityNumber: string,
    loading: boolean,  ctx: StateContext<LegalEntityPracticeV3InvoiceStateModel> 
    ) {
    const newLoading = new Map(ctx.getState().loading);
    newLoading.set(legalEntityNumber, loading);
    ctx.patchState({
      loading: newLoading
    });
    }
}
